import React, { useMemo } from "react";
import {
  useGetDailyActivityStats,
  useGetSignupAnalytics,
  useGetSignupFunnel,
  useGetSignupsBySegment,
  useGetTutorialStats,
  useGetToolboxPageAddedEvents,
  useGetToolboxUsageStats,
  useGetToolboxOpenEvents,
  useGetLoginFrequency,
  useGetLoginCountDistribution,
} from "../../../api/queries";
import { UserSegmentEnum } from "../../../types";
import UsageOverTimeView from "./UsageOverTimeView";

interface UsageOverTimeWidgetProps {
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
}

const formatDate = (dateStr: string) => {
  const datePart = dateStr.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const formatHour = (hour: number) => {
  if (hour === 0) return "12 AM";
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return "12 PM";
  return `${hour - 12} PM`;
};

const UsageOverTimeWidget: React.FC<UsageOverTimeWidgetProps> = ({
  selectedSegments,
  startDate,
  endDate,
}) => {
  const isSingleDay = useMemo(() => {
    if (!startDate || !endDate) return false;
    const tzOffset = new Date().getTimezoneOffset();
    const s = new Date(new Date(startDate).getTime() - tzOffset * 60 * 1000);
    const e = new Date(new Date(endDate).getTime() - tzOffset * 60 * 1000);
    return (
      s.getUTCFullYear() === e.getUTCFullYear() &&
      s.getUTCMonth() === e.getUTCMonth() &&
      s.getUTCDate() === e.getUTCDate()
    );
  }, [startDate, endDate]);

  // ── Data fetching ────────────────────────────────────────────────────────────

  const { data: activityData, isLoading: loadingActivity } =
    useGetDailyActivityStats(selectedSegments, startDate, endDate);

  const { data: signupData, isLoading: loadingSignups } = useGetSignupAnalytics(
    selectedSegments,
    startDate,
    endDate,
    isSingleDay ? "hour" : "day",
  );

  const { data: funnelData, isLoading: loadingFunnel } = useGetSignupFunnel(
    selectedSegments,
    startDate,
    endDate,
  );

  const { data: segmentData, isLoading: loadingSegments } =
    useGetSignupsBySegment(selectedSegments, startDate, endDate);

  const { data: loginMetrics, isLoading: loadingLoginMetrics } =
    useGetLoginFrequency(selectedSegments, startDate, endDate);

  const {
    data: loginCountDistribution,
    isLoading: loadingLoginCountDistribution,
  } = useGetLoginCountDistribution(selectedSegments, startDate, endDate);

  const { data: tutorialStats, isLoading: loadingTutorial } =
    useGetTutorialStats(selectedSegments, startDate, endDate);

  const { data: toolboxPageAdded, isLoading: loadingToolboxAdded } =
    useGetToolboxPageAddedEvents(selectedSegments, startDate, endDate);

  const { data: toolboxUsage, isLoading: loadingToolboxUsage } =
    useGetToolboxUsageStats(selectedSegments, startDate, endDate);

  const { data: toolboxOpenEvents, isLoading: loadingToolboxOpenEvents } =
    useGetToolboxOpenEvents(selectedSegments, startDate, endDate);

  const isLoadingChart = loadingActivity || loadingSignups;
  const isLoadingSignupSection =
    loadingSignups || loadingFunnel || loadingSegments;

  // ── Data transforms ──────────────────────────────────────────────────────────

  const chartData = useMemo(() => {
    const empty = {
      labels: [] as string[],
      logins: [] as number[],
      uniqueLogins: [] as number[],
      pageViews: [] as number[],
      uniquePageViews: [] as number[],
      signups: [] as number[],
      firstLogins: [] as number[],
    };

    if (!activityData) return empty;

    type Entry = {
      logins: number;
      uniqueLogins: number;
      pageViews: number;
      uniquePageViews: number;
      signups: number;
      firstLogins: number;
    };
    const newEntry = (): Entry => ({
      logins: 0,
      uniqueLogins: 0,
      pageViews: 0,
      uniquePageViews: 0,
      signups: 0,
      firstLogins: 0,
    });

    if (isSingleDay) {
      const hourMap = new Map<number, Entry>();
      const getHour = (h: number) => {
        if (!hourMap.has(h)) hourMap.set(h, newEntry());
        return hourMap.get(h)!;
      };
      activityData.logins.forEach((item) => {
        const e = getHour(item.hour ?? 0);
        e.logins = item.count;
        e.uniqueLogins = item.uniqueUserCount;
      });
      activityData.pageViews.forEach((item) => {
        const e = getHour(item.hour ?? 0);
        e.pageViews = item.count;
        e.uniquePageViews = item.uniqueUserCount;
      });
      signupData?.timeline.forEach((item) => {
        const e = getHour(item.hour ?? 0);
        e.signups = item.signups;
        e.firstLogins = item.firstLogins;
      });
      const allHours = Array.from({ length: 24 }, (_, i) => i);
      return {
        labels: allHours.map(formatHour),
        logins: allHours.map((h) => hourMap.get(h)?.logins ?? 0),
        uniqueLogins: allHours.map((h) => hourMap.get(h)?.uniqueLogins ?? 0),
        pageViews: allHours.map((h) => hourMap.get(h)?.pageViews ?? 0),
        uniquePageViews: allHours.map(
          (h) => hourMap.get(h)?.uniquePageViews ?? 0,
        ),
        signups: allHours.map((h) => hourMap.get(h)?.signups ?? 0),
        firstLogins: allHours.map((h) => hourMap.get(h)?.firstLogins ?? 0),
      };
    }

    const dateMap = new Map<string, Entry>();
    const getDay = (key: string) => {
      if (!dateMap.has(key)) dateMap.set(key, newEntry());
      return dateMap.get(key)!;
    };
    activityData.logins.forEach((item) => {
      const e = getDay(item.date.split("T")[0]);
      e.logins = item.count;
      e.uniqueLogins = item.uniqueUserCount;
    });
    activityData.pageViews.forEach((item) => {
      const e = getDay(item.date.split("T")[0]);
      e.pageViews = item.count;
      e.uniquePageViews = item.uniqueUserCount;
    });
    signupData?.timeline.forEach((item) => {
      const e = getDay(item.date.split("T")[0]);
      e.signups = item.signups;
      e.firstLogins = item.firstLogins;
    });

    let minDate: Date | null = null;
    const allKeys = Array.from(dateMap.keys()).sort();
    if (allKeys.length > 0) {
      const [y, m, d] = allKeys[0].split("-").map(Number);
      minDate = new Date(y, m - 1, d);
    }
    if (startDate) {
      const s = new Date(startDate);
      if (!minDate || s < minDate) minDate = s;
    }
    const maxDate = endDate ? new Date(endDate) : new Date();
    maxDate.setHours(0, 0, 0, 0);
    if (!minDate) return empty;

    const allDates: string[] = [];
    const cursor = new Date(minDate);
    while (cursor <= maxDate) {
      allDates.push(
        `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`,
      );
      cursor.setDate(cursor.getDate() + 1);
    }
    return {
      labels: allDates.map(formatDate),
      logins: allDates.map((d) => dateMap.get(d)?.logins ?? 0),
      uniqueLogins: allDates.map((d) => dateMap.get(d)?.uniqueLogins ?? 0),
      pageViews: allDates.map((d) => dateMap.get(d)?.pageViews ?? 0),
      uniquePageViews: allDates.map(
        (d) => dateMap.get(d)?.uniquePageViews ?? 0,
      ),
      signups: allDates.map((d) => dateMap.get(d)?.signups ?? 0),
      firstLogins: allDates.map((d) => dateMap.get(d)?.firstLogins ?? 0),
    };
  }, [activityData, signupData, startDate, endDate, isSingleDay]);

  const signupTimeline = useMemo(() => {
    if (!signupData)
      return {
        labels: [],
        codesEntered: [],
        emailsVerified: [],
        firstLogins: [],
      };
    if (isSingleDay) {
      const hourMap = new Map(
        signupData.timeline.map((item) => [item.hour ?? 0, item]),
      );
      const allHours = Array.from({ length: 24 }, (_, i) => i);
      return {
        labels: allHours.map(formatHour),
        codesEntered: allHours.map((h) => hourMap.get(h)?.codesEntered ?? 0),
        emailsVerified: allHours.map(
          (h) => hourMap.get(h)?.emailsVerified ?? 0,
        ),
        firstLogins: allHours.map((h) => hourMap.get(h)?.firstLogins ?? 0),
      };
    }
    return {
      labels: signupData.timeline.map((item) => formatDate(item.date)),
      codesEntered: signupData.timeline.map((item) => item.codesEntered),
      emailsVerified: signupData.timeline.map((item) => item.emailsVerified),
      firstLogins: signupData.timeline.map((item) => item.firstLogins),
    };
  }, [signupData, isSingleDay]);

  const totalLogins = chartData.logins.reduce((s, v) => s + v, 0);
  const totalPageViews = chartData.pageViews.reduce((s, v) => s + v, 0);
  const daysWithData = Math.max(
    activityData?.logins.length ?? 0,
    activityData?.pageViews.length ?? 0,
    1,
  );
  const avgDailyLogins = Math.round(totalLogins / daysWithData);
  const avgDailyPageViews = Math.round(totalPageViews / daysWithData);

  return (
    <UsageOverTimeView
      isSingleDay={isSingleDay}
      isLoadingChart={isLoadingChart}
      isLoadingSignupSection={isLoadingSignupSection}
      loadingLoginMetrics={loadingLoginMetrics}
      loadingTutorial={loadingTutorial}
      loadingToolboxAdded={loadingToolboxAdded}
      loadingToolboxUsage={loadingToolboxUsage}
      loadingToolboxOpenEvents={loadingToolboxOpenEvents}
      chartData={chartData}
      totalLogins={totalLogins}
      totalPageViews={totalPageViews}
      avgDailyLogins={avgDailyLogins}
      avgDailyPageViews={avgDailyPageViews}
      signupTimeline={signupTimeline}
      totalSignups={signupData?.totalSignups ?? 0}
      totalRedemptions={signupData?.totalRedemptions ?? 0}
      conversionRate={signupData?.conversionRate ?? null}
      funnel={
        funnelData
          ? {
              stage1_codesEntered: funnelData.funnel.stage1_codesEntered,
              stage2_emailsVerified: funnelData.funnel.stage2_emailsVerified,
              stage3_firstLogins: funnelData.funnel.stage3_firstLogins,
              totalDropoffs: funnelData.funnel.totalDropoffs,
              overallConversion: funnelData.rates.overallConversion,
            }
          : null
      }
      segments={segmentData?.segments ?? []}
      totalLoginsMetric={loginMetrics?.totalLogins ?? 0}
      totalUsersMetric={loginMetrics?.totalUsers ?? 0}
      avgLoginFrequency={loginMetrics?.averageLoginFrequency ?? null}
      minLoginCount={loginMetrics?.minLoginCount ?? 0}
      maxLoginCount={loginMetrics?.maxLoginCount ?? 0}
      loginCountBuckets={loginCountDistribution?.buckets ?? []}
      loginCountTotalUsers={loginCountDistribution?.totalUsers ?? 0}
      loadingLoginCountDistribution={loadingLoginCountDistribution}
      tutorialViewedCount={tutorialStats?.tutorialViewedCount ?? 0}
      tutorialVideoWatchedCount={tutorialStats?.tutorialVideoWatchedCount ?? 0}
      totalToolboxOpens={toolboxUsage?.totalOpens ?? 0}
      totalToolboxTopics={toolboxUsage?.totalModules ?? 0}
      pagesAddedToToolbox={toolboxPageAdded?.totalAdds ?? 0}
      uniqueToolboxUsers={toolboxPageAdded?.totalUniqueUsers ?? 0}
      toolboxCreationStats={(toolboxUsage?.toolboxStats ?? []).map((row) => ({
        _id: row._id,
        topicTitle: row.moduleTitle,
        totalUsers: row.totalUsers,
        totalOpens: row.totalOpens,
      }))}
      toolboxOpenEvents={(toolboxOpenEvents?.openEvents ?? []).map((event) => ({
        _id: event._id,
        topicTitle: event.moduleTitle,
        totalOpens: event.totalOpens,
        sources: event.sources,
      }))}
      toolboxOpenEventsTotalOpens={toolboxOpenEvents?.totalOpens ?? 0}
      toolboxOpenEventsTotalTopics={toolboxOpenEvents?.totalModules ?? 0}
    />
  );
};

export default UsageOverTimeWidget;
